import { useWhitelistContext } from '../providers/WhitelistProvider/WhitelistProvider';
import { getApiToken, isValidEmailFormat, regexValidation } from '../utils/cob-utils';
import { useRef } from 'react';
interface IPostObject {
    method: string;
    headers: {
        'Content-Type': string;
        Authorization: string;
    };
    body: string;
    signal?: AbortController['signal'];
}

export type TBriteVerifyResponse = {
    email?: {
        address: string;
        account: string;
        domain: string;
        status: 'accept_all' | 'invalid' | string;
        connected: null;
        disposable: boolean;
        role_address: boolean;
        error_code: 'email_account_invalid' | string;
        error: 'Email account invalid' | string;
    },
    phone?: {
        number: string;
        service_type: null | string;
        phone_location: null | string;
        status: 'invalid' | string;
        errors: string[];
    },
};

export interface IEmailResponse {
    duration: number;
    email: TBriteVerifyResponse['email'];
}

export interface IPhoneResponse {
    duration: number;
    phone: TBriteVerifyResponse['phone'];
}

type ValidationResult = {
    value: string,
    responseJson: IPhoneResponse | IEmailResponse,
    result: 'reject' | 'resolve'
};

/**
 * Brite Verify is a company used to verify phone numbers and email addresses
 *
 * Brite Verify sends a 200 for invalid responses as well
 */
export default function useBriteVerify() {
    const { isPhoneWhitelisted, isEmailWhitelisted } = useWhitelistContext();
    const checkedValues = useRef<ValidationResult[]>([]);
    const syntaxRegexRef = useRef(regexValidation());

    const isWhitelisted = (toCheck: { email?: string, phone?: string }): boolean => {
        if (toCheck?.email) {
            return isEmailWhitelisted(toCheck.email);
        }
        if (toCheck?.phone) {
            return isPhoneWhitelisted(toCheck.phone);
        }
        return false;
    };

    const getBriteVerifyToken = () => {
        const token = getApiToken('briteVerifyKey');
        return token;
    };

    const isSyntaxInvalid = (toCheck: { email?: string, phone?: string }): boolean => {
        const { email, phone } = toCheck;
        if (!email && !phone) {
            return true;
        }
        if (
            email
            && (
                !isValidEmailFormat(email)
                || syntaxRegexRef.current?.disallow.email.test(email)
            )
        ) {
            return true;
        }
        if (
            phone
        ) {
            const sanitisedPhone = phone.replaceAll('-', '');
            if (
                sanitisedPhone.length !== 10
                || !syntaxRegexRef.current.allow.phone.test(sanitisedPhone)
                || syntaxRegexRef.current.disallow.phone.test(sanitisedPhone)
            ) {
                return true;
            }
        }
        return false;
    };

    const wasTestedBefore = (toCheck: { email?: string, phone?: string }) => {
        if (toCheck?.email) {
            return checkedValues.current.find(previousResult => previousResult.value === toCheck.email);
        }
        if (toCheck?.phone) {
            return checkedValues.current.find(previousResult => previousResult.value === toCheck.phone);
        }
        return false;
    };

    const briteVerify = async (
        body: string,
        options?: {
            signal?: AbortController['signal'];
            /**
             * this boolean toggles whether or not a request should be sent at all
             */
            shouldSendRequest?: boolean;
        },
    ): Promise<IEmailResponse | IPhoneResponse | string> => {
        const toCheck: { email?: string, phone?: string } = JSON.parse(body);
        if (isWhitelisted(toCheck)) {
            return Promise.resolve('skipped');
        }
        if (isSyntaxInvalid(toCheck)) {
            return Promise.reject('invalid syntax');
        }
        const previousResult = wasTestedBefore(toCheck);
        if (previousResult) {
            if (previousResult.result === 'resolve') {
                return Promise.resolve(previousResult?.responseJson);
            }
            return Promise.reject('Previously Evaluated');

        }

        const briteVerifyKey = getBriteVerifyToken();

        const postObject: IPostObject = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `ApiKey: ${briteVerifyKey}`,
            },
            body,
        };

        if (options) {
            const { signal, shouldSendRequest } = options;

            if (!shouldSendRequest) {
                return Promise.resolve('skipped');
            }

            if (signal) {
                postObject.signal = signal;
            }
        }

        const response = await fetch(
            'https://bpi.briteverify.com/api/public/v1/fullverify',
            postObject,
        );

        if (!response || !response.ok) {
            // Developer Note: This needs to resolve successfully 
            //   in order to prevent blocking acquisitions if BV is down
            return Promise.resolve('Error in API');
        }

        if (body.includes('@')) {
            const responseJson = await response.json();
            const {
                email: { error, error_code, status },
            }: IEmailResponse = responseJson;

            if (status !== 'unknown' && (error || error_code || status === 'invalid')) {
                checkedValues.current.push({
                    value: toCheck.email!,
                    responseJson: null,
                    result: 'reject',
                });
                return Promise.reject('invalid email');
            }

            checkedValues.current.push({
                value: toCheck.email!,
                responseJson: responseJson,
                result: 'resolve',
            });
            return Promise.resolve(responseJson);
        }

        const responseJson = await response.json();
        const {
            phone: { errors, status },
        }: IPhoneResponse = responseJson;

        if (status !== 'unknown' && (errors.length > 0 || status === 'invalid')) {
            checkedValues.current.push({
                value: toCheck.phone!,
                responseJson: null,
                result: 'reject',
            });
            return Promise.reject('invalid phone number');
        }

        checkedValues.current.push({
            value: toCheck.phone!,
            responseJson: responseJson,
            result: 'resolve',
        });
        return Promise.resolve(responseJson);
    };

    const emailValidationApi = async (email: string): Promise<IEmailResponse | false> => {
        try {
            return await briteVerify(JSON.stringify({ email })) as IEmailResponse;
        } catch (error) {
            return false;
        }
    };

    const phoneValidationApi = async (phone: string): Promise<IPhoneResponse | 'skipped' | false> => {
        try {
            return await briteVerify(JSON.stringify({ phone })) as IPhoneResponse;
        } catch (error) {
            return false;
        }
    };

    return {
        briteVerify,
        emailValidationApi,
        phoneValidationApi,
    };
}
